import { Alert, Button, message, Select, Typography } from 'antd'
import { RolodexIndication } from 'features/indicationTriage'
import {
  COMPONENT_TYPE_INFO,
  CORROSION_INFO,
  corrosionNote,
  describeDerivedCorrosion,
  describeDiscontinuityProximity,
  ENTRY_ROUTE_LABELS,
  REMOTENESS_INFO,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsClassificationContent'
import { FACT_ORDER } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsClassificationUi'
import {
  ClassificationFacts,
  classifyComponentType,
  extractClassificationFacts,
  seedClassificationFacts,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsComponentClassification'
import {
  computeLtaDecision,
  deriveCorrosion,
  LtaDecision,
  PermittedPartLevel,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsLtaDecision'
import {
  CorrosionType,
  FfsRegionClassification,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionClassificationSchema'
import {
  buildRegionClassification,
  reportAffectingClassificationEquals,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionRouting'
import { DerivedRemoteness } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRemoteness'
import { FfsPart } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import {
  describeDerivedNps,
  npsFromOuterDiameterMeters,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/npsFromOuterDiameter'
import { useFfsDisplayUnits } from 'features/indicationTriage/cards/ffsCalc/useFfsDisplayUnits'
import { CitedHelp } from 'features/libra/stepComponents/ffs/CitedHelp'
import { ClassificationFactInput } from 'features/libra/stepComponents/ffs/ClassificationFactInput'
import {
  ComponentTypeDefinitionsReference,
  EligibilityMatrixReference,
  Fig45BandsReference,
} from 'features/libra/stepComponents/ffs/FfsClassificationReference'
import {
  Label,
  Row,
  SELECT_STYLE,
} from 'features/libra/stepComponents/ffs/ltaFormLayout'
import { useLtaProfileStatus } from 'features/libra/stepComponents/ffs/useLtaProfileStatus'
import { FC, Fragment, useMemo, useState } from 'react'
import {
  GeckoComponentModel,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

const CORROSION_OPTIONS = [
  { value: 'general', label: 'General corrosion (point readings valid)' },
  { value: 'local', label: 'Local metal loss (thickness profiles required)' },
  { value: 'groove-like', label: 'Groove-like flaw — not covered here' },
]

/** Display form for a permitted part+level — wording belongs to the view. */
const partLevelLabel = ({ part, level }: PermittedPartLevel): string =>
  `${part === FfsPart.PART_4 ? 'Part 4' : 'Part 5'} ${
    level === 'level1' ? 'L1' : 'L2'
  }`

/**
 * The outcome banner: one flat, exhaustive switch over the decision state rather
 * than nested ternaries with repeated `resolvedType && entryRoute` re-tests.
 */
const DecisionAlert: FC<{ decision: LtaDecision }> = ({ decision }) => {
  switch (decision.kind) {
    case 'groove-not-covered':
      return (
        <Alert
          type='warning'
          showIcon
          message='This region is a groove-like flaw — not covered by this tool.'
          description='A groove-like flaw is assessed by a separate API 579 Part 5 procedure that this workflow does not implement. It is not routed to a Part 4 or Part 5 assessment here.'
        />
      )
    case 'profile-loading':
      return (
        <Alert
          type='info'
          showIcon
          message='Checking for a critical-thickness profile for this LTA…'
        />
      )
    case 'blocked-on-profile':
      return (
        <Alert
          type='warning'
          showIcon
          message='Local metal loss requires a critical-thickness profile for this LTA.'
          description='No usable profile was found. (Re)generate critical-thickness profiles in the LTA Generation step before this region can be routed to a Part 5 assessment.'
        />
      )
    case 'incomplete':
      return (
        <Alert
          type='info'
          showIcon
          message='Answer the questions above to determine the component type.'
        />
      )
    case 'routed': {
      const typeInfo = COMPONENT_TYPE_INFO[decision.componentType]
      return (
        <Alert
          type='success'
          showIcon
          message={
            <>
              {typeInfo.title} <CitedHelp note={typeInfo} />
            </>
          }
          description={
            <>
              <div>Recommended: {ENTRY_ROUTE_LABELS[decision.route]}.</div>
              {decision.permittedOthers.length > 0 ? (
                <div>
                  Also permitted:{' '}
                  {decision.permittedOthers.map(partLevelLabel).join(', ')}.
                </div>
              ) : null}
            </>
          }
        />
      )
    }
  }
}

export interface LtaClassificationFormProps {
  indication: RolodexIndication
  /**
   * This LTA's already-parsed saved decision (`null` when unclassified). Passed
   * in rather than re-parsed here — the step owns the single parse.
   */
  savedClassification: FfsRegionClassification | null
  componentId: string | undefined
  inspectionId: string | undefined
  componentModel: GeckoComponentModel | undefined
  /**
   * Distance to this LTA's nearest modelled feature (`null` when there are no
   * usable rulers). ADVISORY ONLY — it can raise a proximity warning against the
   * region default but never sets it, so this may arrive after mount and may be
   * absent entirely without affecting the form.
   */
  remoteness: DerivedRemoteness | null
  decidedBy: string
  readonly: boolean
  isSaving: boolean
  saveClassification: (
    indicationId: string,
    classification: FfsRegionClassification,
    opts?: { profileAvailable?: boolean }
  ) => Promise<void>
}

/**
 * The per-LTA classification form. Corrosion type is derived from the LTA's own
 * `customInfo.ffsStats` (minimum & mean wall thickness, converted to mm) by
 * `deriveCorrosion`; the routing/gating outcome is one `LtaDecision` computed by
 * `computeLtaDecision`. Both are pure and unit-tested, so this component is only
 * state, wiring, and presentation.
 */
export const LtaClassificationForm: FC<LtaClassificationFormProps> = ({
  indication,
  savedClassification,
  componentId,
  inspectionId,
  componentModel,
  remoteness,
  decidedBy,
  readonly,
  isSaving,
  saveClassification,
}) => {
  const [messageApi, messageContextHolder] = message.useMessage()
  const { thickness: displayUnit } = useFfsDisplayUnits()
  const profileStatus = useLtaProfileStatus(indication.id)

  const { thickness, lengths, basis, derivedCorrosion } = useMemo(
    () =>
      deriveCorrosion({
        rawFfsStats: indication.customInfo?.ffsStats,
        selection: indication.selection,
      }),
    [indication.customInfo?.ffsStats, indication.selection]
  )

  // Seeded from the saved decision (this component remounts per LTA via `key`,
  // so these initializers re-run for each selection). A saved decision ALWAYS
  // wins; `seedClassificationFacts` supplies opening answers only, and every
  // fact it sets stays editable. Nothing here waits on the rulers — see that
  // function's docblock for why the region default is not ruler-derived.
  const [facts, setFacts] = useState<ClassificationFacts>(() =>
    savedClassification
      ? savedClassification.classificationFacts
      : seedClassificationFacts(componentModel)
  )
  // The effective corrosion type is the engineer's override (seeded from a saved
  // decision) if present, else the synchronously-derived value.
  const [corrosionOverride, setCorrosionOverride] =
    useState<CorrosionType | null>(
      () => savedClassification?.corrosionType ?? null
    )
  const corrosionType = corrosionOverride ?? derivedCorrosion

  const corrosionOverridden =
    corrosionOverride !== null &&
    derivedCorrosion !== null &&
    corrosionOverride !== derivedCorrosion

  // Readout prose renders in the preferred unit (`displayUnit`); the pure helpers
  // compare in mm.
  const corrosionReadout = describeDerivedCorrosion(
    {
      basis,
      tMmMm: thickness.tMmMm,
      tAmMm: thickness.tAmMm,
      lengthMm: lengths.lengthMm,
      widthMm: lengths.widthMm,
      overridden: corrosionOverridden,
    },
    displayUnit
  )

  // `null` when nothing is close enough to flag, or when there is nothing to
  // measure. Both are silence — this check only ever contradicts the default.
  const proximityWarning = describeDiscontinuityProximity(
    {
      lmsdMm: remoteness?.lmsdMm ?? null,
      nearestFeatureTitle: remoteness?.nearestFeatureTitle ?? null,
      thresholdMm: remoteness?.thresholdMm ?? null,
    },
    displayUnit
  )

  // Auto-derived from the component's outer diameter (Fig 4.5 piping NPS).
  // `facts.nps` is the OVERRIDE slot; the effective value used for
  // classification/display is the manual entry if present, else the
  // OD-derived one. Only `nps` differs between `facts` and `effectiveFacts`.
  // Only piping has an NPS (Fig 4.5). Gating on `shellGeometry` keeps the
  // derived value -- and the NPS row it surfaces via `effectiveFacts` -- off
  // non-piping geometries whose OD happens to match a standard pipe size.
  const derivedNps = useMemo(
    () =>
      facts.shellGeometry === 'piping'
        ? npsFromOuterDiameterMeters(facts.D)
        : null,
    [facts.shellGeometry, facts.D]
  )

  const effectiveFacts = useMemo<ClassificationFacts>(
    () =>
      (facts.nps === undefined || facts.nps === null) && derivedNps !== null
        ? { ...facts, nps: derivedNps }
        : facts,
    [facts, derivedNps]
  )

  const npsOverridden =
    facts.nps !== undefined &&
    facts.nps !== null &&
    derivedNps !== null &&
    facts.nps !== derivedNps

  const classification = useMemo(
    () => classifyComponentType(effectiveFacts),
    [effectiveFacts]
  )

  const decision = useMemo(
    () => computeLtaDecision({ classification, corrosionType, profileStatus }),
    [classification, corrosionType, profileStatus]
  )

  const factsToShow = useMemo<Array<keyof ClassificationFacts>>(() => {
    const answered = FACT_ORDER.filter((k) => effectiveFacts[k] !== undefined)
    const missing =
      classification.status === 'more-data'
        ? (classification.missing as Array<keyof ClassificationFacts>)
        : []
    const set = new Set<keyof ClassificationFacts>([
      'region',
      ...answered,
      ...missing,
    ])
    return FACT_ORDER.filter((k) => set.has(k))
  }, [effectiveFacts, classification])

  const setFact = (key: keyof ClassificationFacts, value: unknown) =>
    setFacts((prev) => {
      // Changing `region` resets downstream facts so a stale value can't leak
      // into a different branch -- but re-seed from the asset model, so
      // model-derived geometry (D, shellGeometry) survives and doesn't have to be
      // re-entered by hand. Saved answers are deliberately NOT restored here:
      // they were specific to the previous region.
      if (key === 'region')
        return {
          ...extractClassificationFacts(componentModel),
          region: value as ClassificationFacts['region'],
        }
      return { ...prev, [key]: value }
    })

  // Committable when the decision is either `routed` (an assessable Part/
  // Level) OR `groove-not-covered` (a groove-like flaw, which must still be
  // PERSISTED — with entryRoute GROOVE_NOT_COVERED — so the eligibility gate
  // locks both levels rather than treating this LTA as unclassified).
  const committable =
    decision.kind === 'routed' || decision.kind === 'groove-not-covered'
  const canSave = committable && !readonly && !!componentId && !!inspectionId

  const handleSave = async () => {
    if (
      (decision.kind !== 'routed' && decision.kind !== 'groove-not-covered') ||
      !canSave ||
      corrosionType === null
    )
      return
    // `decidedAt` is the close-out STALENESS anchor: a report computed before it
    // is treated as stale. Only bump it when a report-affecting field (type /
    // corrosion / route) actually changes — an idempotent re-save, or a corrected
    // fact label that doesn't move the route, must NOT strand every prior report.
    const decidedAt =
      savedClassification &&
      reportAffectingClassificationEquals(savedClassification, {
        componentType: decision.componentType,
        corrosionType,
        entryRoute: decision.route,
      })
        ? savedClassification.decidedAt
        : new Date().toISOString()
    // Built through `buildRegionClassification` rather than as a literal so the
    // derived-but-persisted fields (`eligibility`, `profileRequirement`) come from
    // the canonical derivations — see their docblock in
    // FfsRegionClassificationSchema for why they are stored at all.
    const classificationRecord = buildRegionClassification({
      componentType: decision.componentType,
      corrosionType,
      entryRoute: decision.route,
      classificationFacts: effectiveFacts,
      decidedBy,
      decidedAt,
    })
    try {
      await saveClassification(indication.id, classificationRecord, {
        profileAvailable: profileStatus === 'usable',
      })
      messageApi.success(`Classified ${indication.title}.`)
    } catch {
      messageApi.error('Failed to save the classification.')
    }
  }

  return (
    <>
      {messageContextHolder}
      <Typography.Title level={5}>
        Classify: {indication.title}
      </Typography.Title>
      {factsToShow.map((k) => (
        <Fragment key={k}>
          <ClassificationFactInput
            fact={k}
            value={effectiveFacts[k]}
            readonly={readonly}
            onChange={setFact}
          />
          {/*
            An Alert, not the grey caption used for the corrosion readout. Shell
            body is the only offered region and it opens the Fig 4.3/4.4/4.5
            gates, so every LTA reaches those gates without the engineer
            choosing anything — this flag is the only signal that the underlying
            remoteness assumption may not hold. It has to be hard to miss.
            Absent when nothing is close enough to flag.
          */}
          {k === 'region' && proximityWarning ? (
            <Alert
              type='warning'
              showIcon
              message={
                <>
                  {proximityWarning.title} <CitedHelp note={REMOTENESS_INFO} />
                </>
              }
              description={proximityWarning.detail}
            />
          ) : null}
          {/*
            Grey caption mirroring the corrosion readout's tone: NPS is
            auto-derived from the component's outer diameter (Fig 4.5 piping
            band), and this says so -- or that it was overridden -- rather than
            leaving the prefilled/edited value unexplained. Absent when there
            is no OD-derived NPS to report.
          */}
          {k === 'nps'
            ? (() => {
                const odInches =
                  facts.D !== undefined && Number.isFinite(facts.D)
                    ? GeckoMeasurement.CreateFromValue(
                        facts.D,
                        GeckoMeasurementUnit.Meters
                      ).convertTo(GeckoMeasurementUnit.Inches).num
                    : null
                const caption = describeDerivedNps({
                  derivedNps,
                  odInches,
                  overridden: npsOverridden,
                })
                return caption ? (
                  <Typography.Text
                    type='secondary'
                    style={{ fontSize: '0.75rem' }}>
                    {caption}
                  </Typography.Text>
                ) : null
              })()
            : null}
        </Fragment>
      ))}

      <Row style={{ alignItems: 'flex-start' }}>
        <Label>
          Corrosion type{' '}
          {corrosionType ? (
            <CitedHelp note={corrosionNote(corrosionType)} />
          ) : (
            <CitedHelp note={CORROSION_INFO.rule} />
          )}
        </Label>
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <Select
            style={SELECT_STYLE}
            placeholder='Select corrosion type'
            value={corrosionType ?? undefined}
            onChange={(v) => setCorrosionOverride(v as CorrosionType)}
            options={CORROSION_OPTIONS}
            disabled={readonly}
          />
          <Typography.Text type='secondary' style={{ fontSize: '0.75rem' }}>
            {corrosionReadout.kind === 'derived' ? (
              <>
                <strong>{corrosionReadout.label}</strong>
                {corrosionReadout.detail}
              </>
            ) : (
              corrosionReadout.message
            )}
          </Typography.Text>
        </div>
      </Row>

      <DecisionAlert decision={decision} />

      <Row style={{ gap: '1rem', flexWrap: 'wrap' }}>
        <ComponentTypeDefinitionsReference />
        <EligibilityMatrixReference />
        {facts.shellGeometry === 'piping' ? <Fig45BandsReference /> : null}
      </Row>

      <Row style={{ justifyContent: 'flex-end' }}>
        <Button
          type='primary'
          loading={isSaving}
          disabled={!canSave}
          onClick={handleSave}>
          Save Classification
        </Button>
      </Row>
    </>
  )
}
